// Epoch-envelope retention (2026-09-14) + the envelope budget (2026-10-08).
//
// Every epoch bump seals the new epoch SECRET to every member — one envelope
// per (epoch, member) — and until 2026-09-14 those accumulated forever. An
// epoch key only matters while a record encrypted under it exists, and
// records live 15 days server-side (events-v3 / images-v3 TTL and the images
// bucket lifecycle); the Windows client discards un-uploaded captures after
// 14 days too.
//
// So every minted epoch envelope carries `epochSince` (ms). An epoch is
// PRUNABLE once it stopped being current more than EPOCH_RETENTION_MS ago,
// judged by the SUCCESSOR epoch's mint time — never the epoch's own (a
// year-old epoch that is still current must survive). Envelopes written
// before the stamp existed simply lack it; such an epoch is pruned once any
// LATER epoch's known mint time is past the window (an upper bound on when
// it stopped being current).
//
// The 60-day window is four times the record lifetime. The residual risk it
// accepts: an uploader that stays offline for more than 60 days keeps
// encrypting under the epoch key it last fetched, and whatever it uploads
// after that is unreadable (its own 14-day discard bounds the loss to its
// last two weeks). The Decryptor reports such a record as "no epoch key".
//
// 2026-10-08: the service stores the whole set as ONE document and lands it
// in the same transaction as the member list, so the former 100-item
// transaction budget, the delta planner and the withdraw-only shrink writes
// are gone. What remains is a plain count ceiling (the service's
// MAX_ADK_ENVELOPES), far above anything pruning lets accumulate.
//
// Pure module (no Vue / API imports) so Node can exercise it directly.

export const EPOCH_RETENTION_MS = 60 * 24 * 60 * 60 * 1000

/** The service's ceiling on one account's envelope set — mirrors lib/validate.js MAX_ADK_ENVELOPES. */
export const MAX_ADK_ENVELOPES = 1000

export const rowKey = (e) => `${e.keyEpoch}#${e.userId}`

/** epoch -> the earliest `epochSince` (ms) any of its envelopes carries, for epochs that carry one. */
export function epochSinceMap (envelopes) {
  const since = new Map()
  for (const e of envelopes || []) {
    if (!Number.isSafeInteger(e.epochSince) || e.epochSince <= 0) continue
    const prev = since.get(e.keyEpoch)
    if (prev === undefined || e.epochSince < prev) since.set(e.keyEpoch, e.epochSince)
  }
  return since
}

/**
 * The epochs whose envelopes can be withdrawn: older than the current epoch,
 * and some later epoch's known mint time is past the retention window.
 */
export function prunableEpochs (envelopes, { currentEpoch, nowMs = Date.now(), retentionMs = EPOCH_RETENTION_MS } = {}) {
  const since = epochSinceMap(envelopes)
  const epochs = [...new Set((envelopes || []).map((e) => e.keyEpoch))].sort((a, b) => a - b)
  const cutoff = nowMs - retentionMs
  const out = []
  for (const epoch of epochs) {
    if (currentEpoch !== undefined && epoch >= currentEpoch) continue
    // Upper bound on when this epoch stopped being current: the earliest
    // known mint time among the epochs after it.
    let endedBy
    for (const [other, ms] of since) {
      if (other > epoch && (endedBy === undefined || ms < endedBy)) endedBy = ms
    }
    if (endedBy !== undefined && endedBy <= cutoff) out.push(epoch)
  }
  return out
}

/** Split `envelopes` into the ones to keep and the epochs being dropped. */
export function pruneEnvelopes (envelopes, opts) {
  const drop = new Set(prunableEpochs(envelopes, opts))
  return {
    kept: (envelopes || []).filter((e) => !drop.has(e.keyEpoch)),
    prunedEpochs: [...drop]
  }
}

/**
 * Refuse up front what the service would refuse: a set over the ceiling.
 * Only reachable with pruning defeated (hundreds of epochs inside 60 days),
 * so the message asks for the one thing that shrinks the set.
 */
export function assertEnvelopeBudget (envelopes, cap = MAX_ADK_ENVELOPES) {
  const n = (envelopes || []).length
  if (n > cap) {
    throw new Error(`This change needs ${n} key envelopes and the limit is ${cap}. `
      + 'Remove report recipients you no longer need and try again.')
  }
}
